import { Wine, WineFormData } from '@/types/wine';

/**
 * Configuration for duplicate wine matching
 */
export interface DuplicateMatchConfig {
  nameSimilarityThreshold: number;
  producerSimilarityThreshold: number;
  requireExactType: boolean;
  requireExactVintage: boolean;
  requireExactDenomination: boolean;
}

/**
 * Default configuration for duplicate matching
 * Configurazione più permissiva: focus sul nome del vino
 */
const DEFAULT_CONFIG: DuplicateMatchConfig = {
  nameSimilarityThreshold: 75, // Abbassato da 85 a 75 per essere più permissivi
  producerSimilarityThreshold: 70, // Non più obbligatorio, solo suggerimento
  requireExactType: true,
  requireExactVintage: false,
  requireExactDenomination: false,
};

/**
 * Normalizes a string for matching by:
 * - Converting to lowercase
 * - Removing accents
 * - Removing punctuation
 * - Normalizing whitespace
 * - Trimming
 */
export const normalizeForMatching = (text: string | undefined): string => {
  if (!text) return '';

  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .replace(/[.,;:!?'"()\[\]{}]/g, '') // Remove punctuation
    .replace(/\s+/g, ' ') // Normalize multiple spaces to single space
    .trim();
};

/**
 * Calculates Levenshtein distance between two strings
 * Returns the minimum number of single-character edits required
 * to change one string into the other
 */
export const levenshteinDistance = (s1: string, s2: string): number => {
  const len1 = s1.length;
  const len2 = s2.length;

  // Create a 2D array for dynamic programming
  const matrix: number[][] = Array(len1 + 1)
    .fill(null)
    .map(() => Array(len2 + 1).fill(0));

  // Initialize first column and row
  for (let i = 0; i <= len1; i++) {
    matrix[i][0] = i;
  }
  for (let j = 0; j <= len2; j++) {
    matrix[0][j] = j;
  }

  // Fill the matrix
  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      const cost = s1[i - 1] === s2[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1, // Deletion
        matrix[i][j - 1] + 1, // Insertion
        matrix[i - 1][j - 1] + cost // Substitution
      );
    }
  }

  return matrix[len1][len2];
};

/**
 * Calculates similarity percentage between two strings
 * Returns a value from 0 to 100, where 100 is identical
 */
export const calculateSimilarity = (s1: string, s2: string): number => {
  if (!s1 && !s2) return 100; // Both empty = identical
  if (!s1 || !s2) return 0; // One empty = completely different

  const distance = levenshteinDistance(s1, s2);
  const maxLength = Math.max(s1.length, s2.length);

  if (maxLength === 0) return 100;

  return ((maxLength - distance) / maxLength) * 100;
};

/**
 * Checks if two wines are duplicates based on the matching algorithm
 * Algoritmo permissivo: se il nome corrisponde, ignora produttore e regione
 */
export const isDuplicateWine = (
  wine1: Wine | WineFormData,
  wine2: Wine,
  config: DuplicateMatchConfig = DEFAULT_CONFIG
): boolean => {
  // 1. Check wine type - must be exact match
  if (config.requireExactType && wine1.type !== wine2.type) {
    return false;
  }

  // 2. Check name similarity - CRITERIO PRINCIPALE
  const normalizedName1 = normalizeForMatching(wine1.name);
  const normalizedName2 = normalizeForMatching(wine2.name);
  const nameSimilarity = calculateSimilarity(normalizedName1, normalizedName2);

  // Se il nome non corrisponde abbastanza, non è un duplicato
  if (nameSimilarity < config.nameSimilarityThreshold) {
    return false;
  }

  // 3. Se il nome corrisponde, ignoriamo produttore e regione
  // (come richiesto dall'utente per allargare le maglie)

  // 4. Check vintage SOLO se richiesto e presente in entrambi
  // Questo fornisce una conferma aggiuntiva ma non è obbligatorio
  if (config.requireExactVintage && wine1.vintage && wine2.vintage) {
    if (wine1.vintage !== wine2.vintage) {
      return false;
    }
  }

  // 5. Ignoriamo completamente denomination, produttore e regione
  // Il focus è sul nome del vino

  // All checks passed - this is a duplicate!
  return true;
};

/**
 * Finds a duplicate wine in the existing wines array
 * Only checks wines in cellar that are owned by the current user
 * Returns the first duplicate found, or null if no duplicate exists
 */
export const findDuplicateWine = (
  newWineData: WineFormData,
  existingWines: Wine[],
  currentUserId: string,
  config: DuplicateMatchConfig = DEFAULT_CONFIG
): Wine | null => {
  // Filter wines to only check:
  // 1. Wines in cellar (status === 'cellar')
  // 2. Wines owned by the current user
  const cellarWines = existingWines.filter(
    wine => wine.status === 'cellar' && wine.ownerId === currentUserId
  );

  // Find the first duplicate
  for (const wine of cellarWines) {
    if (isDuplicateWine(newWineData, wine, config)) {
      return wine;
    }
  }

  return null;
};
